// ============================================================
// TaskFlow OS - Workflow Editor Page
// React Flow based visual editor with liquid glass nodes
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type Connection,
  type NodeTypes,
  type EdgeTypes,
  BackgroundVariant,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import GlassModal from '../components/ui/GlassModal';
import OperationalNodeComponent from '../components/nodes/OperationalNodeComponent';
import ApprovalNodeComponent from '../components/nodes/ApprovalNodeComponent';
import DesignNodeComponent from '../components/nodes/DesignNodeComponent';
import AnimatedEdge from '../components/edges/AnimatedEdge';
import { useProjectStore } from '../stores/projectStore';
import { useWorkflowDataStore } from '../stores/workflowStore';
import { useAuthStore } from '../stores/authStore';
import { subscribeToWorkflow } from '../lib/supabase';
import { getAIService } from '../ai/integration-points';
import { NODE_SPACING_X, NODE_SPACING_Y } from '../lib/constants';
import type { NodeStatus, PriorityLevel, WorkflowType } from '../lib/types';
import { IconPlus, IconCpu, IconRefresh } from '../icons/SvgIcons';

const nodeTypes: NodeTypes = {
  operational: OperationalNodeComponent,
  approval: ApprovalNodeComponent,
  design: DesignNodeComponent,
};

const edgeTypes: EdgeTypes = {
  animated: AnimatedEdge,
};

const WorkflowEditor: React.FC = () => {
  const { workflowId } = useParams<{ projectId: string; workflowId: string }>();
  const { currentWorkflow, fetchWorkflow } = useProjectStore();
  const {
    operationalNodes, approvalNodes, designNodes, edges: dbEdges,
    fetchAllForWorkflow, createOperationalNode, createApprovalNode, createDesignNode,
    createEdge, updateOperationalNode,
  } = useWorkflowDataStore();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [showAddNodeModal, setShowAddNodeModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateText, setGenerateText] = useState('');
  const [generating, setGenerating] = useState(false);

  const [newNodeTitle, setNewNodeTitle] = useState('');
  const [newNodeDesc, setNewNodeDesc] = useState('');
  const [newNodeType, setNewNodeType] = useState<WorkflowType>('operational');
  const [newNodePriority, setNewNodePriority] = useState<PriorityLevel>('medium');

  useEffect(() => {
    if (workflowId) {
      fetchWorkflow(workflowId);
      fetchAllForWorkflow(workflowId);
    }
  }, [workflowId]);

  useEffect(() => {
    if (!workflowId) return;
    const unsubscribe = subscribeToWorkflow(workflowId, {
      onNodeChange: () => fetchAllForWorkflow(workflowId),
      onEdgeChange: () => fetchAllForWorkflow(workflowId),
    });
    return unsubscribe;
  }, [workflowId]);

  useEffect(() => {
    const flowNodes: Node[] = [];

    operationalNodes.forEach((node) => {
      flowNodes.push({
        id: node.id,
        type: 'operational',
        position: { x: node.position_x, y: node.position_y },
        data: {
          label: node.title,
          status: node.status,
          priority: node.priority,
          description: node.description,
          assigneeName: (node.assignee as any)?.display_name,
          assigneeAvatar: (node.assignee as any)?.avatar_url,
          dueDate: node.due_date,
          tags: node.tags,
          onEdit: () => {},
          onStatusChange: (status: NodeStatus) => {
            updateOperationalNode(node.id, { status });
          },
        },
      });
    });

    approvalNodes.forEach((node) => {
      flowNodes.push({
        id: node.id,
        type: 'approval',
        position: { x: node.position_x, y: node.position_y },
        data: {
          label: node.title,
          approvalStatus: node.approval_status,
          approverName: (node.approver as any)?.display_name,
          approverAvatar: (node.approver as any)?.avatar_url,
          deadline: node.deadline,
          rejectionReason: node.rejection_reason,
        },
      });
    });

    designNodes.forEach((node) => {
      flowNodes.push({
        id: node.id,
        type: 'design',
        position: { x: node.position_x, y: node.position_y },
        data: {
          label: node.title,
          status: node.status,
          designType: node.node_type,
          assigneeName: (node.assignee as any)?.display_name,
          version: node.version,
        },
      });
    });

    setNodes(flowNodes);

    const flowEdges: Edge[] = dbEdges.map((e) => ({
      id: e.id,
      source: e.source_node_id,
      target: e.target_node_id,
      type: 'animated',
      data: { isActive: e.is_active, label: e.label },
    }));

    setEdges(flowEdges);
  }, [operationalNodes, approvalNodes, designNodes, dbEdges]);

  const onConnect = useCallback(async (connection: Connection) => {
    if (!workflowId || !connection.source || !connection.target) return;
    await createEdge({
      workflow_id: workflowId,
      source_node_id: connection.source,
      source_node_type: 'operational',
      target_node_id: connection.target,
      target_node_type: 'operational',
      is_active: true,
    });
  }, [workflowId, createEdge]);

  const onNodeDragStop = useCallback(async (_event: any, node: Node) => {
    const dbNode = operationalNodes.find((n) => n.id === node.id);
    if (dbNode) {
      await updateOperationalNode(node.id, {
        position_x: node.position.x,
        position_y: node.position.y,
      });
    }
  }, [operationalNodes, updateOperationalNode]);

  const handleAddNode = async () => {
    if (!workflowId || !newNodeTitle.trim()) return;
    const posX = Math.random() * 400 + 100;
    const posY = Math.random() * 300 + 100;

    if (newNodeType === 'operational') {
      await createOperationalNode({
        workflow_id: workflowId,
        title: newNodeTitle,
        description: newNodeDesc || undefined,
        priority: newNodePriority,
        position_x: posX,
        position_y: posY,
      });
    } else if (newNodeType === 'approval') {
      await createApprovalNode({
        workflow_id: workflowId,
        title: newNodeTitle,
        description: newNodeDesc || undefined,
        position_x: posX,
        position_y: posY,
      });
    } else if (newNodeType === 'design') {
      await createDesignNode({
        workflow_id: workflowId,
        title: newNodeTitle,
        description: newNodeDesc || undefined,
        position_x: posX,
        position_y: posY,
      });
    }

    setNewNodeTitle('');
    setNewNodeDesc('');
    setShowAddNodeModal(false);
  };

  const handleGenerate = async () => {
    if (!workflowId || !generateText.trim()) return;
    setGenerating(true);
    try {
      const aiService = getAIService();
      const result = await aiService.generateWorkflow({ prompt: generateText });
      const parsed = result.workflow;
      const nodeIdMap = new Map<string, string>();
      let x = 100;
      let y = 100;

      for (const pNode of parsed.nodes) {
        const res = await createOperationalNode({
          workflow_id: workflowId,
          title: pNode.title,
          description: pNode.description,
          priority: pNode.priority || 'medium',
          position_x: x,
          position_y: y,
        });
        if (res.data) nodeIdMap.set(pNode.id, res.data.id);
        x += NODE_SPACING_X;
        if (x > 1200) { x = 100; y += NODE_SPACING_Y; }
      }

      for (const pEdge of parsed.edges) {
        const sourceId = nodeIdMap.get(pEdge.source);
        const targetId = nodeIdMap.get(pEdge.target);
        if (sourceId && targetId) {
          await createEdge({
            workflow_id: workflowId,
            source_node_id: sourceId,
            source_node_type: 'operational',
            target_node_id: targetId,
            target_node_type: 'operational',
            label: pEdge.label,
            is_active: true,
          });
        }
      }
      setGenerateText('');
      setShowGenerateModal(false);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Header
        title={currentWorkflow?.name || 'ワークフローエディタ'}
        subtitle={currentWorkflow?.description || ''}
        actions={
          <div className="flex gap-2">
            <button onClick={() => setShowGenerateModal(true)} className="glass-btn flex items-center gap-2 text-sm">
              <IconCpu size={14} /> テキスト生成
            </button>
            <button onClick={() => setShowAddNodeModal(true)} className="glass-btn-primary flex items-center gap-2 text-sm">
              <IconPlus size={14} /> ノード追加
            </button>
          </div>
        }
      />

      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDragStop={onNodeDragStop}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{ type: 'animated' }}
          fitView
          className="bg-transparent"
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="rgba(255, 255, 255, 0.03)" />
          <Controls />
          <MiniMap
            nodeColor={(n) => {
              switch (n.type) {
                case 'operational': return '#10b981';
                case 'approval': return '#f59e0b';
                case 'design': return '#8b5cf6';
                default: return '#6b7280';
              }
            }}
            maskColor="rgba(0, 0, 0, 0.7)"
          />
          <Panel position="top-left">
            <div className="glass-panel p-3 rounded-xl flex items-center gap-4 text-xs">
              <span className="text-white/40">ノード: <span className="text-white font-medium">{nodes.length}</span></span>
              <span className="text-white/40">エッジ: <span className="text-white font-medium">{edges.length}</span></span>
            </div>
          </Panel>
        </ReactFlow>
      </div>

      <GlassModal
        isOpen={showAddNodeModal}
        onClose={() => setShowAddNodeModal(false)}
        title="ノードを追加"
        subtitle="新しいノードをワークフローに追加します"
        footer={
          <>
            <button onClick={() => setShowAddNodeModal(false)} className="glass-btn text-sm">キャンセル</button>
            <button onClick={handleAddNode} className="glass-btn-primary text-sm">追加</button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5">ノードタイプ</label>
            <select value={newNodeType} onChange={(e) => setNewNodeType(e.target.value as WorkflowType)} className="glass-select">
              <option value="operational">実行タスク</option>
              <option value="approval">承認</option>
              <option value="design">設計</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">タイトル</label>
            <input type="text" value={newNodeTitle} onChange={(e) => setNewNodeTitle(e.target.value)} className="glass-input" placeholder="タスク名を入力" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">説明</label>
            <textarea value={newNodeDesc} onChange={(e) => setNewNodeDesc(e.target.value)} className="glass-textarea" placeholder="詳細を入力（任意）" />
          </div>
          {newNodeType === 'operational' && (
            <div>
              <label className="block text-xs text-white/50 mb-1.5">優先度</label>
              <select value={newNodePriority} onChange={(e) => setNewNodePriority(e.target.value as PriorityLevel)} className="glass-select">
                <option value="low">低</option>
                <option value="medium">中</option>
                <option value="high">高</option>
                <option value="critical">緊急</option>
              </select>
            </div>
          )}
        </div>
      </GlassModal>

      <GlassModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        title="テキストからワークフロー生成"
        subtitle="テキストを入力すると、ノードとエッジが自動生成されます"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowGenerateModal(false)} className="glass-btn text-sm">キャンセル</button>
            <button onClick={handleGenerate} disabled={generating} className="glass-btn-primary text-sm flex items-center gap-2">
              {generating ? <IconRefresh size={14} className="animate-spin" /> : <IconCpu size={14} />}
              {generating ? '生成中...' : '生成'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
            <p className="text-xs text-blue-400/80">
              矢印記法 (例: 要件定義 → 設計 → 実装) またはリスト形式を使用できます。
            </p>
          </div>
          <textarea
            value={generateText}
            onChange={(e) => setGenerateText(e.target.value)}
            className="glass-textarea min-h-[200px] font-mono text-sm"
            placeholder={`例:\n要件定義 -> 設計\n設計 -> 実装\n実装 -> テスト\nテスト -> デプロイ`}
          />
        </div>
      </GlassModal>
    </>
  );
};

export default WorkflowEditor;